import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { Box, Button, Chip, Grid, Modal, Paper, TextField, Tooltip, Typography, styled, Divider, FormControlLabel, Checkbox } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useFormik } from "formik";
import * as Yup from 'yup';
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { fetchToken } from "../auth/Token";
import Header from "../components/Header";
import { useLocation } from 'react-router-dom';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(2),
  borderStyle: 'groove',
  border: '3px',
  borderColor: 'grey',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.2)',
  marginTop: '10px',
  marginBottom: '10px',
  minHeight: '50px',
  borderRadius: '10px'
}));

const Teams = () => {
  const location = useLocation();
  const society_id = location.state && location.state.society_id;
  const society_name = location.state && location.state.name;
  const [all_users, setUsers] = useState({});
  const [teams, setTeams] = useState([]);
  const [editing_mode, setEditing_mode] = useState(false);
  const [open, setOpen] = useState(false);
  const [open_create, setOpen_create] = useState(false);
  const [selected_team, setSelected_team] = useState(null);

  const currencyFormat = new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
  });

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: '90%', sm: '90%', md: 900 },
    maxHeight: "80vh",
    minHeight: "70vh",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "7px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
    bgcolor: "background.paper",
    borderRadius: "7px",
    boxShadow: 24,
    p: 4,
  };

  const getUsersToAdd = () => {
    toast.promise(
      api.get(`/association/all/users/by/roles/${society_id}`, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading users...",
        success: (response) => {
          setUsers(response.data.roles);
          return "Users loaded successfully";
        },
        error: (error) => {
          console.error(error);
          return "Error loading users, please refresh";
        },
      }
    );
  };

  const getTeams = () => {
    toast.promise(
      api.get('/association/all/teams', {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading teams...",
        success: (response) => {
          setTeams(response.data);
          return "Teams loaded successfully";
        },
        error: (error) => {
          console.error(error);
          return "Error loading teams, please refresh";
        },
      }
    );
  };

  const viewTeam = (team_id) => {
    toast.promise(
      api.get(`/association/get/one/team/${team_id}`, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading team details...",
        success: (response) => {
          setSelected_team(response.data);
          setOpen(true);
          return "Team details loaded successfully";
        },
        error: (error) => {
          console.error(error);
          return "Error loading team details, please try again";
        },
      }
    );
  };

  const handleClose = () => {
    setOpen(false);
    setSelected_team(null);
  };
  
  const handleCloseCreate = () => {
    setOpen_create(false);
    formik.handleReset();
  };

  const addUserToTeam = (user_id) => {
    const data = new FormData();
    data.append("team_id", selected_team.team_id);
    data.append("user_id", user_id);

    toast.promise(
      api.post("/association/add/user/to/team", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
          "Content-Type": "multipart/form-data",
        },
      }),
      {
        loading: "Adding user to team...",
        success: () => {
          viewTeam(selected_team.team_id); // Refresh team details
          getTeams(); 
          return "User added successfully";
        },
        error: (error) => {
          console.error(error);
          return error.response.data.detail;
        },
      }
    );
  };

  const removeUserFromTeam = (user_id) => {
    const data = new FormData();
    data.append("team_id", selected_team.team_id);
    data.append("user_id", user_id);

    toast.promise(
      api.post("/association/remove/user/from/team", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
          "Content-Type": "multipart/form-data",
        },
      }),
      {
        loading: "Removing user from team...",
        success: () => {
          viewTeam(selected_team.team_id); // Refresh team details
          getTeams(); // Update team list for member count
          return "User removed successfully";
        },
        error: (error) => {
          console.error(error);
          return "Error removing user, please try again";
        },
      }
    );
  };

  const deleteTeam = (team_id) => {
    toast.promise(
      api.delete(`/association/delete/team/${team_id}`, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Deleting team...",
        success: () => {
          getTeams();
          return "Team deleted successfully";
        },
        error: (error) => {
          console.error(error);
          return "Error deleting team, please try again";
        },
      }
    );
  };

  const editTeam = (team) => {
    setEditing_mode(true);
    formik.setFieldValue('name', team.team_name);
    formik.setFieldValue('description', team.team_description);
    formik.setFieldValue('can_review_loan_requests', team.can_review_loan_requests);
    formik.setFieldValue('can_review_expense_requests', team.can_review_expense_requests);
    formik.setFieldValue('can_approve_expense_requests', team.can_approve_expense_requests);
    formik.setFieldValue('can_approve_loan_requests', team.can_approve_loan_requests);
    formik.setFieldValue('amount_limit_to_review', team.amount_limit_to_review);
    formik.setFieldValue('team_id', team.team_id);
    toast.info('Setup ready! Edit in team form.');
    setOpen_create(true);
  };

  function formatDateToYearMonthDay(date) {
    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let suffix = "th";
    if (day === 1 || day === 21 || day === 31) suffix = "st";
    else if (day === 2 || day === 22) suffix = "nd";
    else if (day === 3 || day === 23) suffix = "rd";
    return `${day}${suffix} ${month} ${year}`;
  }

  const validationSchema = Yup.object({
    name: Yup.string().required('Team name is required'),
    description: Yup.string().required('Description is required'),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      can_review_loan_requests: false,
      can_review_expense_requests: false,
      can_approve_loan_requests: false,
      can_approve_expense_requests: false,
      amount_limit_to_review: null,
      team_id: null,
    },
    validationSchema,
    onSubmit: (values) => {
      const data = new FormData();
      data.append("name", values.name);
      data.append("description", values.description);
      data.append("can_review_loan_requests", values.can_review_loan_requests);
      data.append("can_review_expense_requests", values.can_review_expense_requests);
      data.append("can_approve_loan_requests", values.can_approve_loan_requests);
      data.append("can_approve_expense_requests", values.can_approve_expense_requests);
      data.append("society_id", society_id);
      if (editing_mode) {
        data.append("team_id", values.team_id);
      }
      if (values.amount_limit_to_review) {
        data.append("amount_limit_to_review", values.amount_limit_to_review);
      }

      toast.promise(
        api.post("/association/create/team", data, {
          headers: {
            Authorization: "Bearer " + fetchToken(),
            "Content-Type": "multipart/form-data",
          },
        }),
        {
          loading: editing_mode ? 'Updating team...' : 'Creating team...',
          success: (response) => {
            setEditing_mode(false);
            formik.handleReset();
            setOpen_create(false);
            getTeams();
            return response.data;
          },
          error: (error) => {
            console.error(error);
            return 'Error, please try again';
          },
        }
      );
    },
  });

  useEffect(() => {
    getUsersToAdd();
    getTeams();
  }, []);

  return (
    <div className="app">
      <main className="content">
        <Box sx={{ m: 5 }}>
          {/* HEADER */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Header title="Teams Manager"/>
          </Box>
          <Box>
            <Typography
              variant="h3"
              sx={{ textAlign: "center", m: 1 }}
              fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
            >
              <b>{society_name || "Society"} Teams</b>
            </Typography>
          </Box>
          {/* Main Content */}
          <Grid container spacing={2} sx={{mb:10}}>
            <Grid item xs={12} md={12}>
              <Button
                onClick={() => setOpen_create(true)}
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 2,
                  m: 1,
                  backgroundColor: "#000",
                  color: "#fff",
                  boxShadow:
                    "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    boxShadow:
                      "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                    backgroundColor: "#14213d",
                  },
                }}
              >
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Create Team</b>
                </Typography>
              </Button>
            </Grid>
            <Grid item xs={12} md={12}>
              <Item>
                <Typography
                  variant="h4"
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  sx={{ textAlign: 'center' }}
                >
                  <b>Teams</b>
                </Typography>
                <Box
                  sx={{
                    overflow: "auto",
                    marginBottom: 10,
                    maxHeight: 340,
                    "&::-webkit-scrollbar": {
                      width: "7px",
                      height: "7px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555",
                    },
                  }}
                >
                  {!teams.length ? (
                    <Typography
                      sx={{ m: 1 }}
                      fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                    >
                      <b>No Teams Available</b>
                    </Typography>
                  ) : null}
                  <Table size="small" aria-label="teams table">
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Name</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Description</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Members</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Loan Review</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Expense Review</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Loan Approval</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Expense Approval</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Amount Limit</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>View</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Edit</b>
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Delete</b>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teams.map((team) => (
                        <TableRow
                          key={team.team_id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            backgroundColor: "#f5f3f4",
                          }}
                        >
                          <TableCell align="left">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{team.team_name}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{team.team_description}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{team.total_members}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{team.can_review_loan_requests ? 'Yes' : 'No'}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{team.can_review_expense_requests ? 'Yes' : 'No'}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{team.can_approve_loan_requests ? 'Yes' : 'No'}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{team.can_approve_expense_requests ? 'Yes' : 'No'}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{team.amount_limit_to_review?currencyFormat.format(team.amount_limit_to_review): 'N/A'}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Button
                              size="small"
                              color="success"
                              variant="contained"
                              aria-label="View team details"
                              sx={{
                                borderRadius: 2,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                "&:hover": {
                                  boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                  backgroundColor: "#14213d",
                                },
                              }}
                              onClick={() => viewTeam(team.team_id)}
                            >
                              <BusinessCenterRoundedIcon />
                            </Button>
                          </TableCell>
                          <TableCell align="left">
                            <Button
                              size="small"
                              color="success"
                              variant="contained"
                              aria-label="Edit team"
                              sx={{
                                borderRadius: 2,
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                "&:hover": {
                                  boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                  backgroundColor: "#14213d",
                                },
                              }}
                              onClick={() => editTeam(team)}
                            >
                              <EditNoteRoundedIcon />
                            </Button>
                          </TableCell>
                          <TableCell align="left">
                            <Tooltip title="This action cannot be undone">
                              <Button
                                size="small"
                                color="error"
                                variant="contained"
                                aria-label="Delete team"
                                sx={{
                                  borderRadius: 2,
                                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                  "&:hover": {
                                    boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                    backgroundColor: "#14213d",
                                  },
                                }}
                                onClick={() => deleteTeam(team.team_id)}
                              >
                                <DeleteRoundedIcon />
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Item>
            </Grid>
          </Grid>
          {/* Team Details Modal */}
          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    sx={{ textAlign: 'center' }}
                    variant="h4"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>{selected_team?.team_name || "Team"} Details</b>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Description: {selected_team?.team_description}</b>
                  </Typography>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Members: {selected_team?.total_members}</b>
                  </Typography>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Loan Review: {selected_team?.can_review_loan_requests ? 'Yes' : 'No'}</b>
                  </Typography>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Expense Review: {selected_team?.can_review_expense_requests ? 'Yes' : 'No'}</b>
                  </Typography>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Loan Approval : {selected_team?.can_approve_loan_requests ? 'Yes' : 'No'}</b>
                  </Typography>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Expense Approval: {selected_team?.can_approve_expense_requests ? 'Yes' : 'No'}</b>
                  </Typography>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Amount Limit: {selected_team?.amount_limit_to_review?currencyFormat.format(selected_team?.amount_limit_to_review): 'N/A'}</b>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider variant="middle" sx={{ m: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Team Members</b>
                  </Typography>
                  <Box
                    sx={{
                      overflow: "auto",
                      maxHeight: 200,
                      "&::-webkit-scrollbar": {
                        width: "7px",
                        height: "7px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#888",
                        borderRadius: "10px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#555",
                      },
                    }}
                  >
                    {!selected_team?.team_members?.length ? (
                      <Typography
                        sx={{ m: 1 }}
                        fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                      >
                        <b>No Members</b>
                      </Typography>
                    ) : (
                      <Table size="small" aria-label="team members table">
                        <TableHead>
                          <TableRow>
                            <TableCell align="left">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Name</b>
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Joined Date</b>
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Removed Date</b>
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Status</b>
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Remove</b>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selected_team?.team_members?.map((member) => (
                            <TableRow
                              key={member.member_id}
                              sx={{
                                "&:last-child td, &:last-child th": { border: 0 },
                                backgroundColor: "#f5f3f4",
                              }}
                            >
                              <TableCell align="left">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{member.name}</b>
                                </Typography>
                              </TableCell>
                              <TableCell align="left">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatDateToYearMonthDay(new Date(member.joined_date))}</b>
                                </Typography>
                              </TableCell>
                              <TableCell align="left">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{member.ended_date?formatDateToYearMonthDay(new Date(member.ended_date)):'N/A'}</b>
                                </Typography>
                              </TableCell>
                              <TableCell align="left">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{member.status}</b>
                                </Typography>
                              </TableCell>
                              <TableCell align="left">
                                <Button
                                  size="small"
                                  color="error"
                                  variant="contained"
                                  aria-label={`Remove ${member.firstname} ${member.lastname} from team`}
                                  disabled={member.status !== 'active'}
                                  sx={{
                                    borderRadius: 2,
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                                    "&:hover": {
                                      boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                                      backgroundColor: "#14213d",
                                    },
                                  }}
                                  onClick={() => removeUserFromTeam(member.member_id)}
                                >
                                  <PersonRemoveIcon />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider variant="middle" sx={{ m: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                  >
                    <b>Add Members</b>
                  </Typography>
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {Object.keys(all_users).map((role_id) => (
                      all_users[role_id].map((user) => (
                        <Chip
                          key={user.id}
                          label={
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{`${user.firstName} ${user.lastName} (${user.role_name})`}</b>
                            </Typography>
                          }
                          onClick={() => addUserToTeam(user.id)}
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            m: 1,
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                            "&:hover": {
                              boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                              backgroundColor: "green",
                              color: "#fff",
                            },
                          }}
                        />
                      ))
                    ))}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        m: 1,
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                        "&:hover": {
                          boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                          backgroundColor: "#14213d",
                        },
                      }}
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Modal>

          <Modal
            open={open_create}
            onClose={handleCloseCreate}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="h5"
                  sx={{ textAlign: "center", m: 1 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  <b>Team Form {editing_mode ? '(Editing team)' : ''}</b>
                </Typography>
                <TextField
                  size="small"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder="Team Name"
                  fullWidth
                  sx={{ m: 1 }}
                />
                <TextField
                  size="small"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  placeholder="Description"
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ m: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="can_review_loan_requests"
                      checked={formik.values.can_review_loan_requests}
                      onChange={formik.handleChange}
                      color="success"
                    />
                  }
                  label={<Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Can Review Loan Requests</b></Typography>}
                  sx={{ m: .1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="can_review_expense_requests"
                      checked={formik.values.can_review_expense_requests}
                      onChange={formik.handleChange}
                      color="success"
                    />
                  }
                  label={<Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Can Review Expense Requests</b></Typography>}
                  sx={{ m: .1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="can_approve_loan_requests"
                      checked={formik.values.can_approve_loan_requests}
                      onChange={formik.handleChange}
                      color="success"
                    />
                  }
                  label={<Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Can Approve Loan Requests</b></Typography>}
                  sx={{ m: .1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="can_approve_expense_requests"
                      checked={formik.values.can_approve_expense_requests}
                      onChange={formik.handleChange}
                      color="success"
                    />
                  }
                  label={<Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Can Approve Expense Requests</b></Typography>}
                  sx={{ m: .1 }}
                />
                <TextField
                  size="small"
                  name="amount_limit_to_review"
                  type="number"
                  value={formik.values.amount_limit_to_review}
                  onChange={formik.handleChange}
                  placeholder="Amount Limit (for major expense) - optional"
                  fullWidth
                  sx={{ m: 1 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      m: 1,
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor: "#14213d",
                      },
                    }}
                    onClick={handleCloseCreate}
                  >
                    Close
                  </Button>
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      m: 1,
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow: "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor: "#14213d",
                      },
                    }}
                    onClick={formik.handleSubmit}
                    disabled={!formik.values.name || !formik.values.description}
                  >
                    {editing_mode ? "Edit Team" : "Create Team"}
                  </Button>
                </Box>
              </Grid>
            </Box>
          </Modal>
        </Box>
      </main>
    </div>
  );
};

export default Teams;